(() => {
  'use strict';

  const body = document.body;
  if (!body || body.dataset.avCommerceOs !== 'true') return;

  const Australia = window.Australia = window.Australia || {};
  Australia.commerce = Australia.commerce || {};
  const publish = (name, payload) => {
    try { return Australia.analytics?.publish?.(name, payload) === true; } catch (_) { return false; }
  };
  const enabled = (name) => body.dataset[name] === 'true';
  const money = (cents) => {
    const currency = body.dataset.avCurrency || 'BRL';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', { style: 'currency', currency }).format(Number(cents || 0) / 100); }
    catch (_) { return String(Number(cents || 0) / 100); }
  };
  const selectors = () => Array.from(document.querySelectorAll('[data-av42-replenishment]'));
  const currentSelector = (productId, variantId) => selectors().find((node) => {
    const productMatches = !productId || String(node.dataset.avProductId) === String(productId);
    const variantMatches = !variantId || String(node.dataset.avVariantId) === String(variantId);
    return productMatches && variantMatches;
  }) || null;
  const selectedPlanInput = (node = currentSelector()) => node?.querySelector('[data-av42-selling-plan]:checked') || null;
  const managementUrl = () => {
    const value = String(body.dataset.avSubscriptionManagementUrl || '').trim();
    return /^(https?:\/\/|\/)/.test(value) ? value : null;
  };

  Australia.commerce.subscription = Object.freeze({
    isAvailable(productId, variantId) {
      return enabled('avSubscriptionUi') && Boolean(currentSelector(productId, variantId));
    },
    getPlans(productId, variantId) {
      const node = currentSelector(productId, variantId);
      if (!node) return [];
      return Array.from(node.querySelectorAll('[data-av42-selling-plan][data-av-selling-plan-id]')).map((input) => ({
        id: input.dataset.avSellingPlanId || '',
        groupId: input.dataset.avSellingPlanGroupId || '',
        name: input.dataset.avPlanName || '',
        priceCents: Number(input.dataset.avAllocationPriceCents || 0)
      }));
    },
    getSelectedPlan() {
      const input = selectedPlanInput();
      return input?.dataset.avSellingPlanId || null;
    },
    setSelectedPlan(sellingPlanId) {
      const node = currentSelector();
      const input = node?.querySelector(`[data-av42-selling-plan][value="${CSS.escape(String(sellingPlanId || ''))}"]`);
      if (!input) return false;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    },
    getManagementUrl: managementUrl
  });

  const rewardsProvider = () => window.AustraliaRewardsProvider || null;
  const safeProviderCall = (method, fallback = null) => {
    try {
      const provider = rewardsProvider();
      return provider && typeof provider[method] === 'function' ? provider[method]() : fallback;
    } catch (_) { return fallback; }
  };
  Australia.commerce.rewards = Object.freeze({
    isAvailable() { return enabled('avRewardsMarketing') && safeProviderCall('isAvailable', false) === true; },
    getProgramName() { return this.isAvailable() ? safeProviderCall('getProgramName') : null; },
    getBalance() { return this.isAvailable() ? safeProviderCall('getBalance') : null; },
    getTier() { return this.isAvailable() ? safeProviderCall('getTier') : null; },
    getWorldProgress() { return this.isAvailable() ? safeProviderCall('getWorldProgress') : null; },
    openProgram() { return this.isAvailable() ? safeProviderCall('openProgram', false) === true : false; },
    getAccountUrl() { return this.isAvailable() ? safeProviderCall('getAccountUrl') : null; }
  });

  let lastSubscription = null;
  const syncSubscriptionPrice = (node, input) => {
    const productInfo = node.closest('product-info') || node.closest('[data-product-info]');
    const priceNodes = Array.from(productInfo?.querySelectorAll('[data-product-price], [data-au-floatbar-price]') || []);
    if (!priceNodes.length) return;
    const planId = input?.dataset.avSellingPlanId || '';
    const cents = planId ? input.dataset.avAllocationPriceCents : node.dataset.avOneTimePriceCents;
    priceNodes.forEach((priceNode) => {
      if (cents !== undefined && cents !== '') priceNode.textContent = money(cents);
      priceNode.dataset.avPurchaseMode = planId ? 'subscription' : 'one_time';
    });
  };
  const updateSubscription = (node, emit = false) => {
    const input = selectedPlanInput(node);
    if (!input) return;
    node.querySelectorAll('[data-av42-purchase-choice]').forEach((choice) => {
      choice.classList.toggle('is-selected', choice.contains(input));
    });
    const summary = node.querySelector('[data-av-subscription-summary]');
    const planId = input.dataset.avSellingPlanId || '';
    if (summary) summary.textContent = planId
      ? `${input.dataset.avPlanName || ''} · ${money(input.dataset.avAllocationPriceCents)}`
      : '';
    syncSubscriptionPrice(node, input);
    if (emit) {
      publish('australia:purchase_option_selected', {
        component: 'subscription_selector', source: 'pdp', product_id: node.dataset.avProductId,
        variant_id: node.dataset.avVariantId, mode: planId ? 'subscription' : 'one_time', selling_plan_id: planId
      });
      if (planId) publish('australia:subscription_frequency_selected', {
        component: 'subscription_selector', source: 'pdp', product_id: node.dataset.avProductId,
        variant_id: node.dataset.avVariantId, selling_plan_id: planId,
        selling_plan_group_id: input.dataset.avSellingPlanGroupId || '', plan_name: input.dataset.avPlanName || ''
      });
    }
  };

  const validateSubscriptionSubmit = (event) => {
    const form = event.target.closest?.('form');
    const node = form?.querySelector('[data-av42-replenishment]');
    if (!node) return;
    const selected = selectedPlanInput(node);
    if (node.dataset.avRequiresSellingPlan === 'true' && !selected?.dataset.avSellingPlanId) {
      event.preventDefault();
      const status = node.querySelector('[data-av-subscription-status]');
      if (status) status.textContent = node.dataset.avRequiredMessage || '';
      publish('australia:subscription_error', {
        component: 'subscription_selector', source: 'pdp', product_id: node.dataset.avProductId,
        variant_id: node.dataset.avVariantId, error_code: 'required_plan_missing'
      });
      lastSubscription = null;
      return;
    }
    lastSubscription = selected?.dataset.avSellingPlanId ? {
      at: Date.now(), productId: node.dataset.avProductId, variantId: node.dataset.avVariantId,
      planId: selected.dataset.avSellingPlanId, groupId: selected.dataset.avSellingPlanGroupId || ''
    } : null;
  };
  document.addEventListener('submit', validateSubscriptionSubmit, true);

  const initSubscription = (scope = document) => {
    scope.querySelectorAll?.('[data-av42-replenishment]').forEach((node) => {
      if (node.dataset.avCommerceInitialized === 'true') return;
      node.dataset.avCommerceInitialized = 'true';
      updateSubscription(node);
      node.addEventListener('change', (event) => {
        if (!event.target.matches('[data-av42-selling-plan]')) return;
        requestAnimationFrame(() => updateSubscription(node, true));
      });
      publish('australia:subscription_viewed', {
        component: 'subscription_selector', source: 'pdp', product_id: node.dataset.avProductId,
        variant_id: node.dataset.avVariantId, result_count: node.querySelectorAll('[data-av-selling-plan-id]').length
      });
    });
  };
  const onSubscriptionSuccess = () => {
    if (!lastSubscription || Date.now() - lastSubscription.at > 12000) return;
    publish('australia:subscription_added_to_cart', {
      component: 'subscription_selector', source: 'pdp', product_id: lastSubscription.productId,
      variant_id: lastSubscription.variantId, selling_plan_id: lastSubscription.planId,
      selling_plan_group_id: lastSubscription.groupId
    });
    lastSubscription = null;
  };
  const onSubscriptionError = () => {
    if (!lastSubscription || Date.now() - lastSubscription.at > 12000) return;
    publish('australia:subscription_error', {
      component: 'subscription_selector', source: 'pdp', product_id: lastSubscription.productId,
      variant_id: lastSubscription.variantId, selling_plan_id: lastSubscription.planId, error_code: 'cart_add_failed'
    });
    lastSubscription = null;
  };
  ['theme:product:add', 'theme:product:added', 'av:cart:add'].forEach((name) => document.addEventListener(name, onSubscriptionSuccess));
  ['theme:product:add-error', 'theme:cart:error'].forEach((name) => document.addEventListener(name, onSubscriptionError));

  const observeOnce = (node, eventName, payload, threshold = 0.35) => {
    if (node.dataset.avCommerceObserved === 'true' || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= threshold)) return;
      node.dataset.avCommerceObserved = 'true';
      observer.disconnect();
      publish(eventName, payload);
    }, { threshold });
    observer.observe(node);
  };

  const initMeasuredSurfaces = (scope = document) => {
    scope.querySelectorAll?.('[data-av-proof-cluster]').forEach((node) => observeOnce(node, 'australia:proof_cluster_viewed', {
      component: 'proof_cluster', source: node.dataset.avSource || body.dataset.pageType || 'storefront',
      world: node.dataset.avWorld || body.dataset.avWorld || '', result_count: node.querySelectorAll('[data-av-proof-item]').length
    }));
    scope.querySelectorAll?.('[data-av-cross-world]').forEach((node) => observeOnce(node, 'australia:cross_world_viewed', {
      component: 'cross_world', source: node.dataset.avSource || 'pdp', product_id: node.dataset.avProductId || '',
      world: node.dataset.avWorld || body.dataset.avWorld || '', result_count: node.querySelectorAll('[data-av-cross-world-product]').length
    }));
    scope.querySelectorAll?.('[data-av-search-zero-results]').forEach((node) => observeOnce(node, 'australia:zero_results_viewed', {
      component: 'search', source: 'predictive_search', result_count: 0
    }, 0.2));
  };

  if (document.documentElement.dataset.avCommerceDelegates !== 'true') {
    document.documentElement.dataset.avCommerceDelegates = 'true';
    document.addEventListener('click', (event) => {
      const cross = event.target.closest('[data-av-cross-world-product]');
      const crossLink = event.target.closest('a[href]');
      if (cross && crossLink) publish('australia:cross_world_product_clicked', {
        component: 'cross_world', source: cross.closest('[data-av-cross-world]')?.dataset.avSource || 'pdp',
        product_id: cross.dataset.avProductId || '', variant_id: cross.dataset.avVariantId || '',
        world: cross.dataset.avWorld || '', position: Number(cross.dataset.avPosition || 0)
      });
      const management = event.target.closest('[data-av-subscription-management-link]');
      if (management && managementUrl()) publish('australia:subscription_management_clicked', {
        component: 'subscription_management', source: 'account'
      });
      const predictive = event.target.closest('[data-predictive-search-results] a, #predictive-search-results a');
      if (predictive?.dataset.avResultType && predictive?.dataset.avPosition) publish('australia:predictive_result_clicked', {
        component: 'predictive_search', source: 'header', result_type: predictive.dataset.avResultType,
        position: Number(predictive.dataset.avPosition)
      });
    });
  }

  document.addEventListener('theme:product:add', (event) => {
    const cross = event.detail?.button?.closest?.('[data-av-cross-world-product]');
    if (!cross) return;
    publish('australia:cross_world_added', {
      component: 'cross_world', source: cross.closest('[data-av-cross-world]')?.dataset.avSource || 'cart',
      product_id: cross.dataset.avProductId || '', variant_id: cross.dataset.avVariantId || '',
      world: cross.dataset.avWorld || '', position: Number(cross.dataset.avPosition || 0)
    });
  });

  const init = (scope = document) => {
    initSubscription(scope);
    initMeasuredSurfaces(scope);
  };
  init(document);
  if (typeof window.subscribe === 'function' && window.theme?.PUB_SUB_EVENTS?.variantChange) {
    window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({ data }) => {
      try {
        const sectionId = String(data?.sectionId || '');
        const productInfo = sectionId
          ? document.querySelector(`product-info[data-section="${CSS.escape(sectionId)}"]`)
          : document.querySelector('product-info');
        const currentSlot = productInfo?.querySelector('[data-av-subscription-slot]');
        const nextSlot = data?.html?.querySelector?.('[data-av-subscription-slot]');
        if (!currentSlot || !nextSlot) return;
        currentSlot.replaceWith(nextSlot.cloneNode(true));
        initSubscription(productInfo);
      } catch (_) {}
    });
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('theme:product-info:loaded', (event) => init(event.target));
  if ('MutationObserver' in window) {
    let queued = false;
    new MutationObserver((mutations) => {
      if (queued || !mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => node.nodeType === 1))) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; init(document); });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
